import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/format';
import Reveal from '@/components/site/Reveal';

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    document.title = 'Insights & Blog | Cespedis Commercial Real Estate';
    api.get('/blog').then((res) => setPosts(res.data || [])).finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="blog-list-page">
      <section className="relative isolate overflow-hidden bg-[#144726] pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#144726] via-[#1A5A33] to-[#144726]" />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C6A35B]">Insights</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight">Market reports, leasing playbooks & best practices.</h1>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-white/75">Hyderabad commercial real estate trends, retail leasing insights, and property management best practices from the Cespedis desk.</p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading && <p className="text-slate-500">Loading…</p>}
          {!loading && featured && (
            <Reveal>
              <Link to={`/blog/${featured.slug || featured.id}`} className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="blog-featured-card">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="aspect-[16/10] overflow-hidden lg:aspect-auto"><img src={featured.cover_image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-105" /></div>
                  <div className="flex flex-col justify-center p-8 md:p-12">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A35B]">Featured</p>
                    <h2 className="mt-3 font-display text-2xl md:text-4xl font-semibold text-[#144726] tracking-tight">{featured.title}</h2>
                    <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                    <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(featured.published_at)}</span>
                      <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {featured.author}</span>
                    </div>
                    <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#144726] group-hover:text-[#C6A35B]">Read article <ArrowRight className="h-4 w-4" /></p>
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={i * 60}>
                <Link to={`/blog/${p.slug || p.id}`} className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="blog-list-card">
                  <div className="aspect-[16/10] overflow-hidden"><img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-105" /></div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(p.published_at)}</span>
                      <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {p.author}</span>
                    </div>
                    <h3 className="mt-3 font-display text-xl font-semibold text-[#144726] line-clamp-2">{p.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>
                    <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#144726] group-hover:text-[#C6A35B]">Read article <ArrowRight className="h-4 w-4" /></p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
